import type {
  BeetleRender,
  CharacterRender,
  CrocRender,
  FlyingInsectRender,
  LizardRender,
  SnakeRender,
  TurtleRender,
  WalkingInsectRender,
} from "../../../shared/shop-catalog";
import { drawCustomGrid } from "./CustomArt";

interface SpriteAnim {
  bobTime: number;
  flapTime: number;
}

export function drawCharacterSprite(
  ctx: CanvasRenderingContext2D,
  r: number,
  render: CharacterRender,
  anim: SpriteAnim,
): void {
  switch (render.kind) {
    case "lizard":
      drawLizardKit(ctx, r, render, anim);
      return;
    case "snake":
      drawSnakeKit(ctx, r, render, anim);
      return;
    case "turtle":
      drawTurtleKit(ctx, r, render, anim);
      return;
    case "croc":
      drawCrocKit(ctx, r, render, anim);
      return;
    case "beetle":
      drawBeetleKit(ctx, r, render, anim);
      return;
    case "flying_insect":
      drawFlyingInsectKit(ctx, r, render, anim);
      return;
    case "walking_insect":
      drawWalkingInsectKit(ctx, r, render, anim);
      return;
    case "custom": {
      const bob = Math.sin(anim.bobTime * 4) * 0.6;
      const side = r * 2.4;
      drawCustomGrid(ctx, render.cells, render.grid, -side / 2, -side / 2 + bob, side, side);
      return;
    }
  }
}

function drawLizardKit(
  ctx: CanvasRenderingContext2D,
  r: number,
  s: LizardRender,
  anim: SpriteAnim,
): void {
  // Resolve colors. The CREATOR lizard cycles its whole palette through the
  // hue wheel for a smooth rainbow (offsets keep body parts distinguishable).
  let body = s.body;
  let belly = s.belly;
  let tail = s.tail;
  let legs = s.legs;
  if (s.rainbow) {
    const hue = (performance.now() / 200) % 360;
    body = `hsl(${hue},85%,55%)`;
    belly = `hsl(${(hue + 30) % 360},85%,75%)`;
    tail = `hsl(${(hue + 60) % 360},85%,40%)`;
    legs = `hsl(${(hue + 90) % 360},85%,30%)`;
  }

  // body
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 0.75, 0, 0, Math.PI * 2);
  ctx.fill();

  // belly
  ctx.fillStyle = belly;
  ctx.beginPath();
  ctx.ellipse(0, 4, r * 0.7, r * 0.45, 0, 0, Math.PI * 2);
  ctx.fill();

  // tail
  ctx.fillStyle = tail;
  ctx.beginPath();
  ctx.moveTo(-r * 0.9, 0);
  ctx.lineTo(-r * 1.7, -3 + Math.sin(anim.bobTime * 12) * 2);
  ctx.lineTo(-r * 0.9, 5);
  ctx.closePath();
  ctx.fill();

  // back legs
  ctx.fillStyle = legs;
  ctx.fillRect(-2, r * 0.5, 4, 4);
  ctx.fillRect(-r * 0.5, r * 0.4, 4, 5);

  // features
  if (s.feature === "spikes") {
    ctx.fillStyle = tail;
    for (let i = 0; i < 4; i += 1) {
      const sx = -r * 0.6 + i * (r * 0.35);
      ctx.beginPath();
      ctx.moveTo(sx - 2, -r * 0.6);
      ctx.lineTo(sx, -r * 0.95);
      ctx.lineTo(sx + 2, -r * 0.6);
      ctx.closePath();
      ctx.fill();
    }
  } else if (s.feature === "crest") {
    ctx.fillStyle = tail;
    ctx.beginPath();
    ctx.moveTo(r * 0.2, -r * 0.6);
    ctx.lineTo(r * 0.45, -r * 1.4);
    ctx.lineTo(r * 0.55, -r * 0.6);
    ctx.closePath();
    ctx.fill();
  } else if (s.feature === "halo") {
    ctx.save();
    ctx.strokeStyle = "#ffd700";
    ctx.lineWidth = 1.6;
    ctx.shadowColor = "#fff8a0";
    ctx.shadowBlur = 4;
    ctx.beginPath();
    ctx.ellipse(r * 0.35, -r * 1.15, 7, 2.4, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  } else if (s.feature === "frill") {
    ctx.fillStyle = tail;
    const cx = r * 0.5;
    const cy = -r * 0.2;
    for (let i = -2; i <= 2; i += 1) {
      const a = i * 0.35;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a) * r * 0.6, cy + Math.sin(a) * r * 0.7);
      ctx.lineTo(cx + Math.cos(a + 0.2) * r * 0.6, cy + Math.sin(a + 0.2) * r * 0.7);
      ctx.closePath();
      ctx.fill();
    }
  }

  // eye
  ctx.fillStyle = s.eyeWhite ?? "#ffffff";
  ctx.beginPath();
  ctx.arc(r * 0.45, -r * 0.25, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = s.eyePupil ?? "#000000";
  ctx.beginPath();
  ctx.arc(r * 0.55, -r * 0.25, 1.5, 0, Math.PI * 2);
  ctx.fill();

  // tongue flick
  if (anim.flapTime > 0) {
    ctx.strokeStyle = "#e53935";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(r * 0.9, 0);
    ctx.lineTo(r * 1.5, -2);
    ctx.moveTo(r * 1.5, -2);
    ctx.lineTo(r * 1.7, 0);
    ctx.lineTo(r * 1.5, 2);
    ctx.stroke();
  }
}

function drawSnakeKit(
  ctx: CanvasRenderingContext2D,
  r: number,
  s: SnakeRender,
  anim: SpriteAnim,
): void {
  // sinuous body — single S-curve drawn via stacked ellipses
  const wave = Math.sin(anim.bobTime * 6) * 1.5;

  // body: a long ellipse
  ctx.fillStyle = s.primary;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 1.35, r * 0.45, 0, 0, Math.PI * 2);
  ctx.fill();

  // hood (cobra) — flared widening behind the head
  if (s.hood) {
    ctx.fillStyle = s.primary;
    ctx.beginPath();
    ctx.ellipse(r * 0.4, -2, r * 0.55, r * 0.85, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = s.secondary;
    ctx.beginPath();
    ctx.ellipse(r * 0.4, -2, r * 0.32, r * 0.6, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // belly
  ctx.fillStyle = s.belly;
  ctx.beginPath();
  ctx.ellipse(0, r * 0.18, r * 1.2, r * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();

  // pattern overlay
  if (s.pattern === "diamond") {
    ctx.fillStyle = s.secondary;
    for (let i = -3; i <= 2; i += 1) {
      const x = i * (r * 0.42);
      ctx.beginPath();
      ctx.moveTo(x, -r * 0.3);
      ctx.lineTo(x + r * 0.18, 0);
      ctx.lineTo(x, r * 0.1);
      ctx.lineTo(x - r * 0.18, 0);
      ctx.closePath();
      ctx.fill();
    }
  } else if (s.pattern === "blotches") {
    ctx.fillStyle = s.secondary;
    for (let i = -3; i <= 2; i += 1) {
      ctx.beginPath();
      ctx.ellipse(i * (r * 0.42) + 2, -2, r * 0.18, r * 0.14, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (s.pattern === "zigzag") {
    ctx.strokeStyle = s.secondary;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-r * 1.2, -r * 0.15);
    for (let i = 0; i < 7; i += 1) {
      const x = -r * 1.2 + i * (r * 0.35);
      const y = i % 2 === 0 ? -r * 0.25 : r * 0.05;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  } else if (s.pattern === "stripes") {
    ctx.fillStyle = s.secondary;
    for (let i = -3; i <= 2; i += 1) {
      ctx.fillRect(i * (r * 0.42), -r * 0.4, 2, r * 0.8);
    }
  }

  // head
  ctx.fillStyle = s.primary;
  ctx.beginPath();
  ctx.ellipse(r * 1.05, wave, r * 0.42, r * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();

  // eye
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(r * 1.15, wave - 2, 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.arc(r * 1.2, wave - 2, 0.9, 0, Math.PI * 2);
  ctx.fill();

  // forked tongue on flap
  if (anim.flapTime > 0) {
    ctx.strokeStyle = "#e53935";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(r * 1.45, wave);
    ctx.lineTo(r * 1.8, wave - 1);
    ctx.moveTo(r * 1.8, wave - 1);
    ctx.lineTo(r * 2.0, wave - 2);
    ctx.moveTo(r * 1.8, wave - 1);
    ctx.lineTo(r * 2.0, wave + 1);
    ctx.stroke();
  }
}

function drawTurtleKit(
  ctx: CanvasRenderingContext2D,
  r: number,
  s: TurtleRender,
  anim: SpriteAnim,
): void {
  const headOut = anim.flapTime > 0 ? -1.5 : 0; // pulls in on flap

  // body (visible under shell)
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.ellipse(0, r * 0.45, r * 0.85, r * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();

  // flippers (front + back)
  ctx.fillStyle = s.body;
  const flipperWobble = Math.sin(anim.bobTime * 10) * 1;
  ctx.beginPath();
  ctx.ellipse(-r * 0.7, r * 0.45 + flipperWobble, 4, 2.5, 0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(r * 0.7, r * 0.45 - flipperWobble, 4, 2.5, -0.4, 0, Math.PI * 2);
  ctx.fill();

  // shell dome
  ctx.fillStyle = s.shell;
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.1, r * 1.0, r * 0.7, 0, 0, Math.PI * 2);
  ctx.fill();

  // shell pattern (hex segments)
  ctx.strokeStyle = s.accent;
  ctx.lineWidth = 1.2;
  for (let i = -1; i <= 1; i += 1) {
    ctx.beginPath();
    ctx.moveTo(i * 5, -r * 0.55);
    ctx.lineTo(i * 5, r * 0.35);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.1, r * 0.6, r * 0.4, 0, 0, Math.PI * 2);
  ctx.stroke();

  // head
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.ellipse(r * 0.85 + headOut, r * 0.2, r * 0.3, r * 0.27, 0, 0, Math.PI * 2);
  ctx.fill();

  // eye
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.arc(r * 0.95 + headOut, r * 0.15, 1.2, 0, Math.PI * 2);
  ctx.fill();
}

function drawCrocKit(
  ctx: CanvasRenderingContext2D,
  r: number,
  s: CrocRender,
  anim: SpriteAnim,
): void {
  const jawOpen = anim.flapTime > 0 ? 2 : 0;

  // body
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.ellipse(-r * 0.2, 0, r * 1.0, r * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // tail
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.moveTo(-r * 1.0, -3 + Math.sin(anim.bobTime * 8) * 1.5);
  ctx.lineTo(-r * 1.9, 0);
  ctx.lineTo(-r * 1.0, 4);
  ctx.closePath();
  ctx.fill();

  // ridged scutes along the back
  ctx.fillStyle = s.scute;
  for (let i = 0; i < 5; i += 1) {
    const sx = -r * 1.0 + i * (r * 0.38);
    ctx.beginPath();
    ctx.moveTo(sx - 2, -r * 0.45);
    ctx.lineTo(sx, -r * 0.75);
    ctx.lineTo(sx + 2, -r * 0.45);
    ctx.closePath();
    ctx.fill();
  }

  // belly
  ctx.fillStyle = s.belly;
  ctx.beginPath();
  ctx.ellipse(-r * 0.2, r * 0.18, r * 0.85, r * 0.22, 0, 0, Math.PI * 2);
  ctx.fill();

  // legs
  ctx.fillStyle = s.scute;
  ctx.fillRect(-r * 0.6, r * 0.4, 3, 4);
  ctx.fillRect(r * 0.2, r * 0.4, 3, 4);

  // snout (upper jaw)
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.moveTo(r * 0.5, -r * 0.25);
  ctx.lineTo(r * 1.7, -r * 0.05 - jawOpen);
  ctx.lineTo(r * 0.5, r * 0.05);
  ctx.closePath();
  ctx.fill();

  // lower jaw
  ctx.fillStyle = s.belly;
  ctx.beginPath();
  ctx.moveTo(r * 0.5, r * 0.05);
  ctx.lineTo(r * 1.6, r * 0.05 + jawOpen);
  ctx.lineTo(r * 0.5, r * 0.25);
  ctx.closePath();
  ctx.fill();

  // teeth
  if (anim.flapTime > 0) {
    ctx.fillStyle = "#fff";
    for (let i = 0; i < 4; i += 1) {
      const tx = r * 0.65 + i * (r * 0.22);
      ctx.fillRect(tx, -1, 1.2, 2);
    }
  }

  // eye
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(r * 0.3, -r * 0.4, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.arc(r * 0.4, -r * 0.4, 1.1, 0, Math.PI * 2);
  ctx.fill();
}

function drawBeetleKit(
  ctx: CanvasRenderingContext2D,
  r: number,
  s: BeetleRender,
  anim: SpriteAnim,
): void {
  const wiggle = Math.sin(anim.bobTime * 12) * 1;

  // 6 legs (3 per side)
  ctx.strokeStyle = s.legs;
  ctx.lineWidth = 1.4;
  for (let i = 0; i < 3; i += 1) {
    const bx = -r * 0.55 + i * (r * 0.55);
    ctx.beginPath();
    ctx.moveTo(bx, r * 0.3);
    ctx.lineTo(bx - 3, r * 0.85 + wiggle * (i % 2));
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(bx, -r * 0.3);
    ctx.lineTo(bx - 3, -r * 0.85 - wiggle * (i % 2));
    ctx.stroke();
  }

  // shell (elytra)
  ctx.fillStyle = s.shell;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 0.95, r * 0.7, 0, 0, Math.PI * 2);
  ctx.fill();

  // shell split line
  ctx.strokeStyle = s.accent;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(-r * 0.6, 0);
  ctx.lineTo(r * 0.7, 0);
  ctx.stroke();

  // elytra dots
  ctx.fillStyle = s.accent;
  ctx.beginPath();
  ctx.arc(-r * 0.2, -r * 0.25, 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-r * 0.2, r * 0.25, 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(r * 0.25, -r * 0.25, 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(r * 0.25, r * 0.25, 1.5, 0, Math.PI * 2);
  ctx.fill();

  // head
  ctx.fillStyle = s.legs;
  ctx.beginPath();
  ctx.ellipse(r * 0.85, 0, r * 0.28, r * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();

  // horn (scarab) — vertical horn from head
  if (s.horn) {
    ctx.fillStyle = s.accent;
    ctx.beginPath();
    ctx.moveTo(r * 0.78, -r * 0.25);
    ctx.lineTo(r * 0.92, -r * 0.95);
    ctx.lineTo(r * 1.0, -r * 0.25);
    ctx.closePath();
    ctx.fill();
  }

  // snout (weevil) — long thin proboscis
  if (s.snout) {
    ctx.fillStyle = s.legs;
    ctx.beginPath();
    ctx.moveTo(r * 1.05, -1);
    ctx.lineTo(r * 1.85, -0.5);
    ctx.lineTo(r * 1.05, 2);
    ctx.closePath();
    ctx.fill();
  }

  // antennae
  ctx.strokeStyle = s.legs;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(r * 0.95, -r * 0.2);
  ctx.lineTo(r * 1.25, -r * 0.6);
  ctx.moveTo(r * 0.95, r * 0.2);
  ctx.lineTo(r * 1.25, r * 0.6);
  ctx.stroke();

  // eyes
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(r * 0.95, -r * 0.05, 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(r * 0.95, r * 0.1, 1, 0, Math.PI * 2);
  ctx.fill();
}

function drawFlyingInsectKit(
  ctx: CanvasRenderingContext2D,
  r: number,
  s: FlyingInsectRender,
  anim: SpriteAnim,
): void {
  const flutter = 0.45 + Math.abs(Math.sin(anim.bobTime * 18)) * 0.4;

  // glow (firefly) — drawn first so it's behind
  if (s.glow) {
    ctx.save();
    ctx.globalAlpha = 0.55 + Math.sin(anim.bobTime * 4) * 0.2;
    ctx.fillStyle = s.wing;
    ctx.beginPath();
    ctx.arc(-r * 0.7, r * 0.2, r * 0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // 4 wings — two pairs
  ctx.save();
  ctx.globalAlpha = flutter;
  ctx.fillStyle = s.wing;
  // front pair
  ctx.beginPath();
  ctx.ellipse(r * 0.1, -r * 0.55, r * 0.55, r * 0.3, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(r * 0.1, r * 0.55, r * 0.55, r * 0.3, 0.3, 0, Math.PI * 2);
  ctx.fill();
  // back pair (shorter)
  ctx.beginPath();
  ctx.ellipse(-r * 0.45, -r * 0.45, r * 0.4, r * 0.22, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(-r * 0.45, r * 0.45, r * 0.4, r * 0.22, 0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // body — segmented
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 0.85, r * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();

  // segment bands
  ctx.fillStyle = s.accent;
  for (let i = -2; i <= 2; i += 1) {
    ctx.fillRect(i * (r * 0.3) - 1, -r * 0.3, 2, r * 0.6);
  }

  // furry texture (moth)
  if (s.furry) {
    ctx.strokeStyle = s.accent;
    ctx.lineWidth = 0.8;
    for (let i = 0; i < 8; i += 1) {
      const a = (i / 8) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r * 0.5, Math.sin(a) * r * 0.2);
      ctx.lineTo(Math.cos(a) * r * 0.7, Math.sin(a) * r * 0.35);
      ctx.stroke();
    }
  }

  // stinger
  if (s.stinger) {
    ctx.fillStyle = s.accent;
    ctx.beginPath();
    ctx.moveTo(-r * 0.8, -2);
    ctx.lineTo(-r * 1.3, 0);
    ctx.lineTo(-r * 0.8, 2);
    ctx.closePath();
    ctx.fill();
  }

  // head
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.arc(r * 0.85, 0, r * 0.28, 0, Math.PI * 2);
  ctx.fill();

  // antennae
  const antLen = s.antennaeLong ? r * 0.9 : r * 0.5;
  ctx.strokeStyle = s.accent;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(r * 1.0, -r * 0.15);
  ctx.lineTo(r * 1.0 + antLen * 0.7, -r * 0.15 - antLen * 0.7);
  ctx.moveTo(r * 1.0, r * 0.15);
  ctx.lineTo(r * 1.0 + antLen * 0.7, r * 0.15 + antLen * 0.7);
  ctx.stroke();

  // big eyes
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.arc(r * 0.95, -r * 0.12, 1.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(r * 0.95, r * 0.12, 1.5, 0, Math.PI * 2);
  ctx.fill();
}

function drawWalkingInsectKit(
  ctx: CanvasRenderingContext2D,
  r: number,
  s: WalkingInsectRender,
  anim: SpriteAnim,
): void {
  const wiggle = Math.sin(anim.bobTime * 14) * 1.2;

  // 6 legs (3 per side)
  ctx.strokeStyle = s.accent;
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 3; i += 1) {
    const bx = -r * 0.6 + i * (r * 0.55);
    const phase = i * 2;
    ctx.beginPath();
    ctx.moveTo(bx, r * 0.2);
    ctx.lineTo(bx - 2, r * 0.85 + Math.sin(anim.bobTime * 14 + phase) * 1.2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(bx, -r * 0.2);
    ctx.lineTo(bx - 2, -r * 0.85 - Math.sin(anim.bobTime * 14 + phase) * 1.2);
    ctx.stroke();
  }

  // body — long segmented
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 1.15, r * 0.3, 0, 0, Math.PI * 2);
  ctx.fill();

  // segment lines
  ctx.strokeStyle = s.accent;
  ctx.lineWidth = 0.8;
  for (let i = -2; i <= 2; i += 1) {
    const sx = i * (r * 0.35);
    ctx.beginPath();
    ctx.moveTo(sx, -r * 0.28);
    ctx.lineTo(sx, r * 0.28);
    ctx.stroke();
  }

  // pincers (earwig)
  if (s.pincers) {
    ctx.strokeStyle = s.accent;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(-r * 1.05, -r * 0.15);
    ctx.quadraticCurveTo(-r * 1.4, 0, -r * 1.6, -r * 0.05);
    ctx.moveTo(-r * 1.05, r * 0.15);
    ctx.quadraticCurveTo(-r * 1.4, 0, -r * 1.6, r * 0.05);
    ctx.stroke();
  }

  // head
  ctx.fillStyle = s.body;
  ctx.beginPath();
  ctx.arc(r * 1.1, 0, r * 0.3, 0, Math.PI * 2);
  ctx.fill();

  // antennae
  const antLen = s.antennaeLong ? r * 0.85 : r * 0.45;
  ctx.strokeStyle = s.accent;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(r * 1.25, -r * 0.15);
  ctx.lineTo(r * 1.25 + antLen * 0.7, -r * 0.15 - antLen * 0.7 + wiggle);
  ctx.moveTo(r * 1.25, r * 0.15);
  ctx.lineTo(r * 1.25 + antLen * 0.7, r * 0.15 + antLen * 0.7 - wiggle);
  ctx.stroke();

  // eye
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.arc(r * 1.2, -r * 0.05, 1.2, 0, Math.PI * 2);
  ctx.fill();
}
